import { supabase } from '../lib/supabase'

/**
 * Session Analytics Module
 * Provides reusable functions for calculating metrics from assessment sessions
 */

const sessionAnalytics = {
  /**
   * Calculate average duration from sessions
   * @param {Array} sessions - Array of session objects with timestamps
   * @returns {Object} Duration metrics
   */
  calculateAverageDuration(sessions) {
    if (!sessions || sessions.length === 0) {
      return null
    }

    // Calculate time for each session
    const sessionTimes = sessions.map(session => {
      // Prioritize tracked active time (most accurate for actual work time)
      if (session.total_active_time && session.total_active_time > 0) {
        return session.total_active_time
      }

      // Fall back to total_time if available
      if (session.total_time && session.total_time > 0) {
        return session.total_time
      }

      // Last resort: use timestamp difference (can be inflated due to breaks)
      if (session.started_at && session.completed_at) {
        const start = new Date(session.started_at)
        const end = new Date(session.completed_at)
        const diffSeconds = Math.round((end - start) / 1000)

        // Cap at 3 hours to avoid outliers (e.g., left tab open)
        return Math.min(diffSeconds, 10800)
      }

      return 0
    })

    // Filter out invalid times
    const validTimes = sessionTimes.filter(time => time > 0)

    if (validTimes.length === 0) {
      return null
    }

    // Calculate statistics
    const totalSeconds = validTimes.reduce((sum, time) => sum + time, 0)
    const avgSeconds = totalSeconds / validTimes.length
    const avgMinutes = Math.round(avgSeconds / 60)

    // Calculate median
    const sortedTimes = [...validTimes].sort((a, b) => a - b)
    const medianSeconds = sortedTimes[Math.floor(sortedTimes.length / 2)]
    const medianMinutes = Math.round(medianSeconds / 60)

    // Format time accurately without artificial minimums
    let formatted
    if (avgSeconds < 60) {
      formatted = `~${Math.round(avgSeconds)}s`
    } else if (avgMinutes < 60) {
      const remainingSeconds = Math.round(avgSeconds % 60)
      formatted = remainingSeconds > 0
        ? `~${avgMinutes}m ${remainingSeconds}s`
        : `~${avgMinutes}m`
    } else {
      const hours = Math.floor(avgMinutes / 60)
      const minutes = avgMinutes % 60
      formatted = minutes > 0
        ? `~${hours}h ${minutes}m`
        : `~${hours}h`
    }

    return {
      seconds: Math.round(avgSeconds),
      minutes: avgMinutes,
      medianMinutes,
      sessionCount: validTimes.length,
      formatted,
      range: {
        min: Math.round(Math.min(...validTimes) / 60),
        max: Math.round(Math.max(...validTimes) / 60)
      }
    }
  },

  /**
   * Calculate completion rate from sessions
   * @param {Array} sessions - All sessions for a course
   * @returns {Object} Completion metrics
   */
  calculateCompletionRate(sessions) {
    if (!sessions || sessions.length === 0) {
      return {
        rate: 0,
        completed: 0,
        inProgress: 0,
        total: 0,
        percentage: '0%'
      }
    }

    const completed = sessions.filter(s => s.status === 'completed').length
    const inProgress = sessions.filter(s => s.status === 'in_progress').length
    const rate = sessions.length > 0 ? completed / sessions.length : 0

    return {
      rate,
      completed,
      inProgress,
      total: sessions.length,
      percentage: `${Math.round(rate * 100)}%`
    }
  },

  /**
   * Calculate average OVERALL ACCURACY from completed sessions
   * Uses overall_accuracy (true competency) instead of score (best cycle)
   * @param {Array} sessions - Completed sessions with overall_accuracy
   * @returns {Object} Overall accuracy metrics
   */
  calculateAverageScore(sessions) {
    const completedWithAccuracy = sessions?.filter(s =>
      s.status === 'completed' && s.overall_accuracy !== null && s.overall_accuracy !== undefined
    ) || []

    if (completedWithAccuracy.length === 0) {
      return {
        average: 0,
        min: 0,
        max: 0,
        median: 0,
        count: 0,
        formatted: 'N/A'
      }
    }

    // Use OVERALL ACCURACY - true competency across all learning cycles
    const accuracies = completedWithAccuracy.map(s => s.overall_accuracy)
    const sum = accuracies.reduce((total, accuracy) => total + accuracy, 0)
    const average = sum / accuracies.length

    // Calculate median
    const sortedAccuracies = [...accuracies].sort((a, b) => a - b)
    const median = sortedAccuracies[Math.floor(sortedAccuracies.length / 2)]

    return {
      average: Math.round(average * 10) / 10, // Round to 1 decimal
      min: Math.min(...accuracies),
      max: Math.max(...accuracies),
      median: Math.round(median * 10) / 10,
      count: accuracies.length,
      formatted: `${Math.round(average)}%`
    }
  },

  /**
   * Calculate time breakdown by phase
   * @param {Array} sessions - Sessions with phase time data
   * @returns {Object} Time breakdown by phase
   */
  calculateTimeBreakdown(sessions) {
    const validSessions = sessions?.filter(s => s.status === 'completed') || []

    if (validSessions.length === 0) {
      return {
        initial: 0,
        learning: 0,
        reassessment: 0,
        total: 0,
        formatted: {
          initial: '0 min',
          learning: '0 min',
          reassessment: '0 min',
          total: '0 min'
        }
      }
    }

    // Calculate averages for each phase
    const phases = {
      initial: 0,
      learning: 0,
      reassessment: 0
    }

    validSessions.forEach(session => {
      phases.initial += session.initial_phase_time || 0
      phases.learning += session.learning_phase_time || 0
      phases.reassessment += session.reassessment_phase_time || 0
    })

    // Convert to averages
    const avgPhases = {
      initial: Math.round(phases.initial / validSessions.length),
      learning: Math.round(phases.learning / validSessions.length),
      reassessment: Math.round(phases.reassessment / validSessions.length)
    }

    const total = avgPhases.initial + avgPhases.learning + avgPhases.reassessment

    return {
      ...avgPhases,
      total,
      formatted: {
        initial: `${Math.round(avgPhases.initial / 60)} min`,
        learning: `${Math.round(avgPhases.learning / 60)} min`,
        reassessment: `${Math.round(avgPhases.reassessment / 60)} min`,
        total: `${Math.round(total / 60)} min`
      },
      percentages: {
        initial: total > 0 ? Math.round((avgPhases.initial / total) * 100) : 0,
        learning: total > 0 ? Math.round((avgPhases.learning / total) * 100) : 0,
        reassessment: total > 0 ? Math.round((avgPhases.reassessment / total) * 100) : 0
      }
    }
  },

  /**
   * Get all metrics for a course
   * @param {String} courseId - Course ID
   * @returns {Object} All calculated metrics
   */
  async getSessionMetrics(courseId) {
    try {
      // Fetch all sessions for the course
      const { data: sessions, error } = await supabase
        .from('assessment_sessions')
        .select('*')
        .eq('course_id', courseId)
        .order('started_at', { ascending: false })

      if (error) throw error

      // Get unique learners
      const uniqueLearners = new Set(sessions?.map(s => s.user_email) || [])

      // Calculate all metrics
      const completedSessions = sessions?.filter(s => s.status === 'completed') || []

      // Calculate average cycles to mastery
      const sessionsWithCycles = completedSessions.filter(s => s.cycles_to_mastery)
      const avgCycles = sessionsWithCycles.length > 0
        ? Math.round(sessionsWithCycles.reduce((sum, s) => sum + s.cycles_to_mastery, 0) / sessionsWithCycles.length)
        : 0

      return {
        duration: this.calculateAverageDuration(completedSessions),
        completion: this.calculateCompletionRate(sessions),
        scores: this.calculateAverageScore(completedSessions),
        timeBreakdown: this.calculateTimeBreakdown(completedSessions),
        learnerCount: uniqueLearners.size,
        sessionCount: sessions?.length || 0,
        lastActivity: sessions?.[0]?.started_at || null,
        avgCycles // Average cycles to mastery
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching session metrics:', error)
      return {
        duration: null,
        completion: this.calculateCompletionRate([]),
        scores: this.calculateAverageScore([]),
        timeBreakdown: this.calculateTimeBreakdown([]),
        learnerCount: 0,
        sessionCount: 0,
        lastActivity: null
      }
    }
  },

  /**
   * Get learning-focused metrics for a course
   * @param {String} courseId - Course ID
   * @returns {Object} Learning metrics
   */
  async getLearningMetrics(courseId) {
    try {
      // First get sessions for this course
      const { data: sessions, error: sessionsError } = await supabase
        .from('assessment_sessions')
        .select('id')
        .eq('course_id', courseId)

      if (sessionsError) throw sessionsError

      if (!sessions || sessions.length === 0) {
        return {
          initialPassRate: 0,
          knowledgeGapRate: 0,
          masteryImprovement: null
        }
      }

      const sessionIds = sessions.map(s => s.id)

      // Get question responses for these sessions
      const { data: responses, error } = await supabase
        .from('question_responses')
        .select('*')
        .in('session_id', sessionIds)

      if (error) throw error

      // Variant responses are stored under the variant's own question_id;
      // map them back to the parent so each course question counts once
      const { data: courseQuestions } = await supabase
        .from('questions')
        .select('id, parent_id')
        .eq('course_id', courseId)

      const parentOf = new Map()
      courseQuestions?.forEach(q => {
        if (q.parent_id) parentOf.set(q.id, q.parent_id)
      })
      const mainQuestionId = (id) => parentOf.get(id) || id

      // Calculate metrics
      const initialResponses = responses?.filter(r => r.cycle_number === 1) || []
      const reassessResponses = responses?.filter(r => r.cycle_number > 1) || []

      if (import.meta.env.DEV) console.log('Course metrics calculation:', {
        courseId,
        totalResponses: responses?.length || 0,
        initialResponses: initialResponses.length,
        reassessResponses: reassessResponses.length
      })

      // Initial Pass Rate - percentage correct on first try
      const initialCorrect = initialResponses.filter(r => r.is_correct).length
      const initialPassRate = initialResponses.length > 0
        ? Math.round((initialCorrect / initialResponses.length) * 100)
        : 0

      // Knowledge Gap Rate - percentage marked for learning
      const markedForLearning = initialResponses.filter(r => r.needs_learning).length
      const knowledgeGapRate = initialResponses.length > 0
        ? Math.round((markedForLearning / initialResponses.length) * 100)
        : 0

      // Mastery Improvement - improvement from initial to reassessment
      let masteryImprovement = null
      if (reassessResponses.length > 0) {
        const reassessCorrect = reassessResponses.filter(r => r.is_correct).length
        const reassessRate = (reassessCorrect / reassessResponses.length) * 100
        const initialRate = initialResponses.length > 0
          ? (initialResponses.filter(r => r.is_correct).length / initialResponses.length) * 100
          : 0
        masteryImprovement = Math.round(reassessRate - initialRate)
        masteryImprovement = masteryImprovement > 0 ? `+${masteryImprovement}%` : `${masteryImprovement}%`
      }

      // Question Performance Distribution
      const questionPerformance = new Map()

      // Track each question's performance across cycles, in cycle order so
      // "last attempt" reflects the final cycle rather than DB return order
      const orderedResponses = [...(responses || [])].sort((a, b) =>
        ((a.cycle_number || 1) - (b.cycle_number || 1)) ||
        (new Date(a.answered_at) - new Date(b.answered_at)))

      orderedResponses.forEach(response => {
        const questionKey = mainQuestionId(response.question_id)
        if (!questionPerformance.has(questionKey)) {
          questionPerformance.set(questionKey, {
            firstTryCorrect: false,
            lastAttemptCorrect: false,
            wrongAttempts: 0,
            attempts: 0
          })
        }

        const perf = questionPerformance.get(questionKey)
        perf.attempts++

        if (response.cycle_number === 1 && response.is_correct && !response.needs_learning) {
          perf.firstTryCorrect = true
        }

        if (response.is_correct) {
          perf.lastAttemptCorrect = true
        } else {
          perf.wrongAttempts++
          perf.lastAttemptCorrect = false
        }
      })

      // Calculate distribution aligned with metricsService semantics.
      // Courses only complete once every question is eventually correct, so
      // mastered/learned/struggling deliberately overlap (like the learner
      // completion page): a question needing 2+ tries is both learned and
      // struggling. learnedQuickly is the exclusive middle bucket for charts
      // that must sum to 100%.
      let masteredCount = 0        // correct first try (initial proficiency)
      let learnedCount = 0         // eventually correct after missing initially
      let learnedQuicklyCount = 0  // eventually correct with fewer than 2 wrong attempts
      let strugglingCount = 0      // needed 2+ tries, or never got it right

      questionPerformance.forEach(perf => {
        if (perf.firstTryCorrect) {
          masteredCount++
        } else if (perf.lastAttemptCorrect) {
          learnedCount++
          if (perf.wrongAttempts < 2) learnedQuicklyCount++
        }
        if (perf.wrongAttempts >= 2 || (!perf.firstTryCorrect && !perf.lastAttemptCorrect)) {
          strugglingCount++
        }
      })

      const totalQuestions = questionPerformance.size || 1 // Avoid division by zero

      // Calculate PHE (Predictable Human Error) - wrong answers on cycle 1, excluding "I need to learn"
      const wrongAnswers = initialResponses.filter(r =>
        r.cycle_number === 1 &&
        r.is_correct === false &&
        r.needs_learning === false
      ).length

      const answeredCycle1 = initialResponses.filter(r =>
        r.cycle_number === 1 &&
        r.is_correct !== null &&
        r.needs_learning === false
      ).length

      const phe = answeredCycle1 > 0
        ? Math.round((wrongAnswers / answeredCycle1) * 100)
        : 0

      const questionDistribution = {
        mastered: Math.round((masteredCount / totalQuestions) * 100),
        learned: Math.round((learnedCount / totalQuestions) * 100),
        learnedQuickly: Math.round((learnedQuicklyCount / totalQuestions) * 100),
        struggling: Math.round((strugglingCount / totalQuestions) * 100),
        phe: phe,
        masteredCount,
        learnedCount,
        learnedQuicklyCount,
        strugglingCount
      }

      // Question Difficulty Ranking
      const questionDifficulty = []
      const questionStats = new Map()

      // Calculate stats for each question
      initialResponses.forEach(response => {
        const questionKey = mainQuestionId(response.question_id)
        if (!questionStats.has(questionKey)) {
          questionStats.set(questionKey, {
            attempts: 0,
            correct: 0,
            totalTime: 0
          })
        }
        const stats = questionStats.get(questionKey)
        stats.attempts++
        if (response.is_correct) stats.correct++
        stats.totalTime += response.time_spent || 0
      })

      // Get question text and format difficulty data
      const questionIds = Array.from(questionStats.keys())
      let questionsData = []

      if (questionIds.length > 0) {
        const { data } = await supabase
          .from('questions')
          .select('id, question_text')
          .in('id', questionIds)
        questionsData = data || []
      }

      questionsData?.forEach(question => {
        const stats = questionStats.get(question.id)
        if (stats) {
          questionDifficulty.push({
            id: question.id,
            text: question.question_text,
            successRate: Math.round((stats.correct / stats.attempts) * 100),
            avgTime: Math.round(stats.totalTime / stats.attempts),
            attempts: stats.attempts
          })
        }
      })

      // Sort by difficulty (lowest success rate first)
      questionDifficulty.sort((a, b) => a.successRate - b.successRate)

      return {
        initialPassRate,
        knowledgeGapRate,
        masteryImprovement,
        questionDistribution,
        questionDifficulty: questionDifficulty.slice(0, 5) // Top 5 hardest
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching learning metrics:', error)
      return {
        initialPassRate: 0,
        knowledgeGapRate: 0,
        masteryImprovement: null
      }
    }
  },

  /**
   * Get metrics for multiple courses (batch operation)
   * @param {Array} courseIds - Array of course IDs
   * @returns {Object} Metrics indexed by course ID
   */
  async getBatchMetrics(courseIds) {
    try {
      // Fetch all sessions for all courses in one query
      const { data: sessions, error } = await supabase
        .from('assessment_sessions')
        .select('*')
        .in('course_id', courseIds)

      if (error) throw error

      // Group sessions by course
      const sessionsByCourse = {}
      sessions?.forEach(session => {
        if (!sessionsByCourse[session.course_id]) {
          sessionsByCourse[session.course_id] = []
        }
        sessionsByCourse[session.course_id].push(session)
      })

      // Calculate metrics for each course
      const metrics = {}
      for (const courseId of courseIds) {
        const courseSessions = sessionsByCourse[courseId] || []
        const completedSessions = courseSessions.filter(s => s.status === 'completed')
        const uniqueLearners = new Set(courseSessions.map(s => s.user_email))

        // Calculate average cycles to mastery
        const sessionsWithCycles = completedSessions.filter(s => s.cycles_to_mastery)
        const avgCycles = sessionsWithCycles.length > 0
          ? Math.round(sessionsWithCycles.reduce((sum, s) => sum + s.cycles_to_mastery, 0) / sessionsWithCycles.length)
          : 0

        metrics[courseId] = {
          duration: this.calculateAverageDuration(completedSessions),
          completion: this.calculateCompletionRate(courseSessions),
          scores: this.calculateAverageScore(completedSessions),
          timeBreakdown: this.calculateTimeBreakdown(completedSessions),
          learnerCount: uniqueLearners.size,
          sessionCount: courseSessions.length,
          lastActivity: courseSessions[0]?.started_at || null,
          avgCycles // Average cycles to mastery
        }
      }

      return metrics
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching batch metrics:', error)
      return {}
    }
  }
}

export default sessionAnalytics